// DTO → view-model parsers. Every response goes through here (even though the
// wire is already camelCase) so a future server rename breaks only this file and
// consumers never touch a raw DTO.
import type {
  OwnerPayoutDto,
  PayoutDocumentDto,
  PortfolioStatusDto,
  PropertyDetailDto,
  PropertyDocumentDto,
  PropertyListDto,
  PropertyMediaDto,
  PropertyOwnerDto,
  PropertyRentalDto,
  PropertySettlementDto,
  PropertyStatusCountsDto,
  PropertySummaryDto,
  PropertyWindowExtensionDto,
  SettlementBankAccountDto,
  SettlementFeeDto,
  SettlementPaymentDto,
} from './propertyContracts'
import type {
  OwnerPayout,
  PayoutDocument,
  PortfolioStatus,
  PropertyDetail,
  PropertyDocument,
  PropertyListPage,
  PropertyMedia,
  PropertyOwner,
  PropertyRental,
  PropertySettlement,
  PropertyStatusCounts,
  PropertySummary,
  WindowExtensionProposal,
  SettlementBankAccount,
  SettlementFee,
  SettlementPayment,
} from '../types/propertyTypes'

export function parsePropertySummary(dto: PropertySummaryDto): PropertySummary {
  return {
    id: dto.id,
    propertyRefCode: dto.propertyRefCode ?? null,
    propertyName: dto.propertyName,
    propertyType: dto.propertyType,
    areaName: dto.areaName ?? null,
    city: dto.city,
    priceAgreed: dto.priceAgreed ?? null,
    askingPrice: dto.askingPrice ?? null,
    currencyCode: dto.currencyCode,
    propertyStatus: dto.propertyStatus,
    stage: dto.stage,
    stageStep: dto.stageStep,
    stageStepCount: dto.stageStepCount,
    stageState: dto.stageState,
    offeringProcessingStatus: dto.offeringProcessingStatus ?? null,
    fundingDeadlineAt: dto.fundingDeadlineAt ?? null,
    fundingDaysRemaining: dto.fundingDaysRemaining ?? null,
    isFundingWindowLapsed: dto.isFundingWindowLapsed ?? false,
    goLiveAt: dto.goLiveAt ?? null,
    fundingWindowDays: dto.fundingWindowDays ?? null,
    agreementCompletedAt: dto.agreementCompletedAt ?? null,
    windowClosedAt: dto.windowClosedAt ?? null,
    dldTransferredAt: dto.dldTransferredAt ?? null,
    isSettled: dto.isSettled ?? false,
    promotedAt: dto.promotedAt ?? null,
    listingRequestId: dto.listingRequestId,
  }
}

export function parsePropertyMedia(dto: PropertyMediaDto): PropertyMedia {
  return {
    id: dto.id,
    mediaType: dto.mediaType,
    fileUrl: dto.fileUrl,
    fileName: dto.fileName,
    mimeType: dto.mimeType,
    fileSizeBytes: dto.fileSizeBytes,
    categoryName: dto.categoryName ?? null,
    isCover: dto.isCover ?? false,
    isFeatured: dto.isFeatured ?? false,
    mediaStatus: dto.mediaStatus,
    createdAt: dto.createdAt,
  }
}

export function parsePropertyDocument(dto: PropertyDocumentDto): PropertyDocument {
  return {
    id: dto.id,
    documentType: dto.documentType,
    documentTitle: dto.documentTitle,
    fileUrl: dto.fileUrl,
    fileName: dto.fileName,
    fileSizeBytes: dto.fileSizeBytes,
    mimeType: dto.mimeType,
    documentStatus: dto.documentStatus,
    createdAt: dto.createdAt,
  }
}

export function parsePropertyOwner(dto: PropertyOwnerDto): PropertyOwner {
  return {
    fullName: dto.fullName ?? null,
    ownershipPercentage: dto.ownershipPercentage,
    isPrimary: dto.isPrimary,
    isYou: dto.isYou,
  }
}

export function parsePropertyRental(dto: PropertyRentalDto): PropertyRental {
  return {
    leaseStart: dto.leaseStart ?? null,
    leaseEnd: dto.leaseEnd ?? null,
    annualRentAmount: dto.annualRentAmount ?? null,
    currencyCode: dto.currencyCode,
    ejariFileUrl: dto.ejariFileUrl ?? null,
    ejariFileName: dto.ejariFileName ?? null,
  }
}

export function parsePropertyDetail(dto: PropertyDetailDto): PropertyDetail {
  return {
    ...parsePropertySummary(dto),
    windowExtendedAt: dto.windowExtendedAt ?? null,
    windowExtensionDays: dto.windowExtensionDays ?? 0,
    marketplaceUrl: dto.marketplaceUrl ?? null,
    owners: (dto.owners ?? []).map(parsePropertyOwner),
    rental: dto.rental ? parsePropertyRental(dto.rental) : null,
    addressLine1: dto.addressLine1,
    addressLine2: dto.addressLine2 ?? null,
    stateProvince: dto.stateProvince ?? null,
    buildingName: dto.buildingName ?? null,
    unitNumber: dto.unitNumber ?? null,
    villaNumber: dto.villaNumber ?? null,
    landNumber: dto.landNumber ?? null,
    titleDeedNumber: dto.titleDeedNumber ?? null,
    dubaiMunicipalityNumber: dto.dubaiMunicipalityNumber ?? null,
    actualAreaSqm: dto.actualAreaSqm ?? null,
    numberOfBedrooms: dto.numberOfBedrooms ?? null,
    numberOfBathrooms: dto.numberOfBathrooms ?? null,
    floorNumber: dto.floorNumber ?? null,
    parkingSpaces: dto.parkingSpaces ?? null,
    yearBuilt: dto.yearBuilt ?? null,
    ownershipType: dto.ownershipType ?? null,
    requestRefCode: dto.requestRefCode ?? null,
    propertyShortDescription: dto.propertyShortDescription ?? null,
    propertyDescription: dto.propertyDescription ?? null,
    postalCode: dto.postalCode ?? null,
    latitude: dto.latitude ?? null,
    longitude: dto.longitude ?? null,
    googleMapsUrl: dto.googleMapsUrl ?? null,
    builtUpAreaSqm: dto.builtUpAreaSqm ?? null,
    commonAreaSqm: dto.commonAreaSqm ?? null,
    sharedAreaSqm: dto.sharedAreaSqm ?? null,
    numberOfUnits: dto.numberOfUnits ?? null,
    buildingConfiguration: dto.buildingConfiguration ?? null,
    view: dto.view ?? null,
    isFurnished: dto.isFurnished ?? null,
    hasWhiteGoods: dto.hasWhiteGoods ?? null,
    propertyCondition: dto.propertyCondition ?? null,
    propertyUpgrades: dto.propertyUpgrades ?? null,
    certificateYear: dto.certificateYear ?? null,
    makaniNumber: dto.makaniNumber ?? null,
    dldPermitNumber: dto.dldPermitNumber ?? null,
    ownershipTypeRemainingYears: dto.ownershipTypeRemainingYears ?? null,
    developerName: dto.developerName ?? null,
    projectName: dto.projectName ?? null,
    dldValuation: dto.dldValuation ?? null,
    dldValuationDate: dto.dldValuationDate ?? null,
    sentToVaspAt: dto.sentToVaspAt ?? null,
    withdrawnAt: dto.withdrawnAt ?? null,
    rejectedAt: dto.rejectedAt ?? null,
    submittedAt: dto.submittedAt ?? null,
    approvedAt: dto.approvedAt ?? null,
    listedAt: dto.listedAt ?? null,
    media: (dto.media ?? []).map(parsePropertyMedia),
    documents: (dto.documents ?? []).map(parsePropertyDocument),
    marketingDocuments: (dto.marketingDocuments ?? []).map(parsePropertyDocument),
  }
}

export function parsePropertyList(dto: PropertyListDto): PropertyListPage {
  return {
    rows: dto.items.map(parsePropertySummary),
    pagination: {
      page: dto.pagination.page,
      limit: dto.pagination.limit,
      totalItems: dto.pagination.totalItems,
      totalPages: dto.pagination.totalPages,
    },
  }
}

export function parsePortfolioStatus(dto: PortfolioStatusDto): PortfolioStatus {
  return {
    totalPropertyCount: dto.totalPropertyCount,
    inReviewPropertyCount: dto.inReviewPropertyCount,
    fullyFundedCount: dto.fullyFundedCount,
    liveFundingCount: dto.liveFundingCount,
    totalRaised: dto.totalRaised,
    currencyCode: dto.currencyCode,
  }
}

function parseSettlementFee(dto: SettlementFeeDto): SettlementFee {
  return {
    code: dto.code,
    label: dto.label,
    amount: dto.amount,
    vatAmount: dto.vatAmount,
    vatPercentage: dto.vatPercentage ?? null,
  }
}

function parseSettlementPayment(dto: SettlementPaymentDto): SettlementPayment {
  return {
    paymentStatus: dto.paymentStatus,
    amount: dto.amount,
    currencyCode: dto.currencyCode,
    transferReference: dto.transferReference,
    valueDate: dto.valueDate,
    returnDate: dto.returnDate ?? null,
  }
}

function parseSettlementBankAccount(dto: SettlementBankAccountDto): SettlementBankAccount {
  return {
    bankName: dto.bankName,
    maskedAccountNumber: dto.maskedAccountNumber,
    bankAccountStatus: dto.bankAccountStatus,
  }
}

function parsePayoutDocument(dto: PayoutDocumentDto): PayoutDocument {
  return {
    id: dto.id,
    documentType: dto.documentType,
    documentTitle: dto.documentTitle,
    fileUrl: dto.fileUrl,
    fileName: dto.fileName,
    fileSizeBytes: dto.fileSizeBytes,
    mimeType: dto.mimeType,
    createdAt: dto.createdAt,
  }
}

function parseOwnerPayout(dto: OwnerPayoutDto): OwnerPayout {
  return {
    propertyOwnerId: dto.propertyOwnerId ?? null,
    fullName: dto.fullName ?? null,
    isSeller: dto.isSeller ?? false,
    ownershipPercentage: dto.ownershipPercentage,
    lineStatus: dto.lineStatus ?? null,
    grossSaleAmount: dto.grossSaleAmount ?? null,
    totalFeeAmount: dto.totalFeeAmount ?? null,
    totalVatAmount: dto.totalVatAmount ?? null,
    netPayoutAmount: dto.netPayoutAmount ?? null,
    fees: (dto.fees ?? []).map(parseSettlementFee),
    payment: dto.payment ? parseSettlementPayment(dto.payment) : null,
  }
}

export function parsePropertySettlement(dto: PropertySettlementDto): PropertySettlement {
  return {
    currencyCode: dto.currencyCode,
    isEstimated: dto.isEstimated ?? true,
    settlementStatus: dto.settlementStatus ?? null,
    settlementReference: dto.settlementReference ?? null,
    fullyFundedAt: dto.fullyFundedAt ?? null,
    dldTransferredAt: dto.dldTransferredAt ?? null,
    settledAt: dto.settledAt ?? null,
    agreedPrice: dto.agreedPrice ?? null,
    totalFeeAmount: dto.totalFeeAmount ?? null,
    totalVatAmount: dto.totalVatAmount ?? null,
    netPayoutAmount: dto.netPayoutAmount ?? null,
    fees: (dto.fees ?? []).map(parseSettlementFee),
    owners: (dto.owners ?? []).map(parseOwnerPayout),
    sellerBankAccount: dto.sellerBankAccount
      ? parseSettlementBankAccount(dto.sellerBankAccount)
      : null,
    sellerPaymentReceipt: dto.sellerPaymentReceipt
      ? parsePayoutDocument(dto.sellerPaymentReceipt)
      : null,
    sellerPaymentStatement: dto.sellerPaymentStatement
      ? parsePayoutDocument(dto.sellerPaymentStatement)
      : null,
  }
}

export function parsePropertyStatusCounts(dto: PropertyStatusCountsDto): PropertyStatusCounts {
  return {
    all: dto.all,
    inSetup: dto.inSetup,
    onMarketplace: dto.onMarketplace,
    fullyFunded: dto.fullyFunded,
    settled: dto.settled,
  }
}

export function parseWindowExtensionProposal(
  dto: PropertyWindowExtensionDto,
): WindowExtensionProposal | null {
  const { proposal } = dto
  if (!proposal) return null
  return {
    agreementId: proposal.agreementId,
    listingRequestId: proposal.listingRequestId,
    extensionDays: proposal.extensionDays,
    currentDeadlineAt: proposal.currentDeadlineAt ?? null,
    proposedDeadlineAt: proposal.proposedDeadlineAt ?? null,
    fundingProgressPercentage: proposal.fundingProgressPercentage ?? null,
    note: proposal.note ?? null,
    expiresAt: proposal.expiresAt ?? null,
    ownersAccepted: proposal.ownersAccepted,
    ownersTotal: proposal.ownersTotal,
    ownSignatoryId: proposal.ownSignatoryId ?? null,
    ownSignatureStatus: proposal.ownSignatureStatus ?? null,
    declinedAt: proposal.declinedAt ?? null,
    ownDeclineReason: proposal.ownDeclineReason ?? null,
  }
}
